import {
	Button,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Calendar,
} from '@/components/ui';
import { ButtonFilterOptions } from '@/components/custom';
import { PromotionProductEnum, PromotionStatusEnum } from '@/enums';
import { CalendarIcon, CircleX, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { ISelectOption } from '@/interfaces';
import usePromotions from '../hooks/usePromotions';
import routes from '@/lib/routes';

const PRODUCT_OPTIONS: ISelectOption[] = [
	{ value: PromotionProductEnum.BOOST, title: 'Boost' },
	{ value: PromotionProductEnum.COINS, title: 'Coins' },
	{ value: PromotionProductEnum.INCOGNITO, title: 'Incognito' },
];

const STATUS_OPTIONS: ISelectOption[] = [
	{ value: PromotionStatusEnum.PENDING, title: 'En attente' },
	{ value: PromotionStatusEnum.ACTIVE, title: 'En cours' },
	{ value: PromotionStatusEnum.FINISHED, title: 'Terminé' },
];

type TProps = {
	isLoading: boolean;
	activeFilters: ReturnType<typeof usePromotions>['activeFilters'];
	handleFilterChange: (key: string, values: string[]) => void;
	handlePeriodChange: (from?: string, to?: string) => void;
};

const HeaderPage = ({
	isLoading,
	activeFilters,
	handleFilterChange,
	handlePeriodChange,
}: TProps) => {
	const periodFrom = activeFilters.periodFrom ? new Date(activeFilters.periodFrom) : undefined;
	const periodTo = activeFilters.periodTo ? new Date(activeFilters.periodTo) : undefined;
	const hasPeriod = !!(periodFrom || periodTo);

	return (
		<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
			{/* Filters */}
			<div className="flex items-center gap-2 overflow-x-auto pb-0.5 md:flex-wrap md:overflow-visible scrollbar-hide">
				<ButtonFilterOptions
					disabled={isLoading}
					options={PRODUCT_OPTIONS}
					value={activeFilters.products}
					text="Produits"
					onChange={(opts) => handleFilterChange('products', opts.map(o => o.value as string))}
				/>
				<ButtonFilterOptions
					disabled={isLoading}
					options={STATUS_OPTIONS}
					value={activeFilters.status}
					text="Statut"
					onChange={(opts) => handleFilterChange('status', opts.map(o => o.value as string))}
				/>

				{/* Date range picker */}
				<div className="flex items-center my-auto">
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant={hasPeriod ? 'secondary' : 'ghost'}
								size="sm"
								disabled={isLoading}
								className={cn(
									'border border-dashed text-xs gap-1.5',
									hasPeriod && 'border-r-0 rounded-r-none'
								)}
							>
								<CalendarIcon className="h-3.5 w-3.5" />
								{periodFrom && periodTo
									? `${format(periodFrom, 'dd MMM', { locale: fr })} → ${format(periodTo, 'dd MMM yyyy', { locale: fr })}`
									: periodFrom
										? `Depuis ${format(periodFrom, 'dd MMM yyyy', { locale: fr })}`
										: 'Période'}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="start">
							<Calendar
								mode="range"
								selected={{ from: periodFrom, to: periodTo }}
								onSelect={(range) => {
									handlePeriodChange(
										range?.from?.toISOString(),
										range?.to?.toISOString(),
									);
								}}
								numberOfMonths={2}
								autoFocus
							/>
						</PopoverContent>
					</Popover>
					{hasPeriod && (
						<Button
							className="border border-dashed border-l-0 rounded-l-none text-xs"
							variant="secondary"
							size="sm"
							title="Supprimer le filtre"
							disabled={isLoading}
							onClick={() => handlePeriodChange(undefined, undefined)}
						>
							<CircleX size={16} />
						</Button>
					)}
				</div>
			</div>

			{/* Create button */}
			<a href={routes.nav.marketing.promotions.create.getHref()} target="_blank" rel="noopener noreferrer">
				<Button size="sm" className="shrink-0">
					<Plus className="mr-2 h-4 w-4" />
					Créer une promotion
				</Button>
			</a>
		</div>
	);
};

export default HeaderPage;
